import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Live suggest dropdown for the Glossary page. Ranks matches by:
 *   1) term starts-with query    (highest)
 *   2) alias starts-with query
 *   3) term contains query
 *   4) alias / see-also contains query
 *   5) definition contains query (lowest)
 * Keyboard: ↑ / ↓ move highlight, Enter jumps to that term (or the top match),
 * Esc closes the dropdown. Clicking a row jumps + focuses that term.
 */
export default function GlossarySearchBox({ terms, value, onChange, onJumpTo }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const suggestions = useMemo(() => rank(terms, value).slice(0, 8), [terms, value]);

  useEffect(() => { setHighlight(0); }, [value]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const commit = (term) => {
    if (!term) return;
    setOpen(false);
    onJumpTo(term);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter" && suggestions[0]) { e.preventDefault(); commit(suggestions[0]); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative max-w-md" data-testid="glossary-search-box">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
      <Input
        ref={inputRef}
        data-testid="glossary-search-input"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search terms, synonyms, definitions..."
        className="pl-9 border-slate-300 dark:border-slate-700"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="glossary-suggest-listbox"
      />
      {open && value.trim() && suggestions.length > 0 && (
        <ul
          id="glossary-suggest-listbox"
          data-testid="glossary-suggest-listbox"
          role="listbox"
          className="absolute z-40 mt-1 w-full max-h-96 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.term_id}
              role="option"
              aria-selected={i === highlight}
              data-testid={`glossary-suggest-item-${s.term_id}`}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => { e.preventDefault(); commit(s); }}
              className={`px-3 py-2 cursor-pointer flex items-start gap-2 text-sm ${
                i === highlight
                  ? "bg-sky-50 dark:bg-sky-950/40 text-slate-900 dark:text-slate-100"
                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {highlightMatch(s.term, value)}
                  {s.part_of_speech && (
                    <span className="ml-2 text-slate-400 dark:text-slate-500 italic text-xs">{s.part_of_speech}</span>
                  )}
                </div>
                {s._hitText && (
                  <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 truncate">
                    {s._hitLabel && <span className="uppercase tracking-widest text-[10px] mr-1.5 text-slate-400 dark:text-slate-500">{s._hitLabel}</span>}
                    {highlightMatch(s._hitText, value)}
                  </div>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500 shrink-0 mt-0.5"/>
            </li>
          ))}
        </ul>
      )}
      {open && value.trim() && suggestions.length === 0 && (
        <div
          data-testid="glossary-suggest-empty"
          className="absolute z-40 mt-1 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg px-3 py-3 text-sm text-slate-500 dark:text-slate-400"
        >
          No matches for "{value}"
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Ranking + highlighting helpers
// -----------------------------------------------------------------------------

function rank(terms, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];
  const scored = [];
  for (const t of terms) {
    const term = (t.term || "").toLowerCase();
    const aliases = (t.aliases || []).map((a) => a.toLowerCase());
    const seeAlso = (t.see_also || []).map((a) => a.toLowerCase());
    const defs = (t.definitions && t.definitions.length ? t.definitions : [t.definition || ""])
      .map((d) => (d || "").toLowerCase());

    let score = 0;
    let hitLabel = null;
    let hitText = null;

    if (term.startsWith(q))       { score = 1000; }
    else if (aliases.some(a => a.startsWith(q))) {
      score = 900;
      const hit = (t.aliases || []).find((a) => a.toLowerCase().startsWith(q));
      hitLabel = "alias"; hitText = hit;
    } else if (term.includes(q))   { score = 700; }
    else {
      const aliasHit = (t.aliases || []).find((a) => a.toLowerCase().includes(q));
      const seeHit   = (t.see_also || []).find((a) => a.toLowerCase().includes(q));
      const defHit   = defs.findIndex((d) => d.includes(q));
      if (aliasHit)              { score = 500; hitLabel = "alias";    hitText = aliasHit; }
      else if (seeHit)           { score = 400; hitLabel = "see also"; hitText = seeHit; }
      else if (defHit >= 0)      {
        score = 200;
        hitLabel = "definition";
        hitText = (t.definitions || [t.definition])[defHit];
      }
    }
    if (score > 0) {
      scored.push({ ...t, _score: score, _hitLabel: hitLabel, _hitText: hitText });
    }
  }
  return scored.sort((a, b) => b._score - a._score || a.term.localeCompare(b.term));
}

function highlightMatch(text, query) {
  const t = String(text || "");
  const q = (query || "").trim();
  if (!q) return t;
  const idx = t.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return t;
  return (
    <>
      {t.slice(0, idx)}
      <mark className="bg-yellow-200/70 dark:bg-yellow-500/30 text-inherit rounded px-0.5">
        {t.slice(idx, idx + q.length)}
      </mark>
      {t.slice(idx + q.length)}
    </>
  );
}
